import React, { useState, useMemo } from "react";
import { Form, InputGroup } from "react-bootstrap";
interface Option {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface GroupedOption {
  group: string;
  options: Option[];
}

interface Props {
  options: Option[] | GroupedOption[];
  values: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  isMultiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  height?: string;
}

const SearchableListSelect: React.FC<Props> = ({
  options,
  values,
  onChange,
  isMultiple = true,
  disabled = false,
  placeholder = "Search...",
  height = "220px",
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const isGrouped = useMemo(
    () => options?.length > 0 && "group" in options[0],
    [options],
  );

  const normalizedValues = useMemo(() => values.map(String), [values]);

  const filteredOptions = useMemo(() => {
    if (isGrouped) {
      return (options as GroupedOption[])
        .map((group) => ({
          ...group,
          options: group.options.filter((opt) =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        }))
        .filter((group) => group.options.length > 0);
    }

    return (options as Option[])?.filter((opt) =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm, isGrouped]);

  const handleSelect = (value: string | number) => {
    if (disabled) return;

    const valStr = String(value);

    if (isMultiple) {
      if (normalizedValues.includes(valStr)) {
        onChange(values.filter((v) => String(v) !== valStr));
      } else {
        onChange([...values, value]);
      }
    } else {
      onChange([value]);
    }
  };

  const flatFilteredOptions = useMemo(() => {
    if (isGrouped) {
      return (filteredOptions as GroupedOption[])?.flatMap(
        (group) => group.options,
      );
    }
    return filteredOptions as Option[];
  }, [filteredOptions, isGrouped]);

  const allValues = flatFilteredOptions?.map((opt) => String(opt.value));

  const isAllSelected =
    allValues?.length > 0 &&
    allValues?.every((val) => normalizedValues.includes(val));

  const handleSelectAll = () => {
    if (disabled) return;

    if (isAllSelected) {
      const remaining = values.filter((v) => !allValues.includes(String(v)));
      onChange(remaining);
    } else {
      const newValues = Array.from(
        new Set([...values, ...flatFilteredOptions.map((o) => o.value)]),
      );
      onChange(newValues);
    }
  };

  const renderOption = (option: Option) => {
    const isChecked = normalizedValues.includes(String(option.value));

    return (
      <div
        key={option.value}
        className="form-check px-3 py-1 d-flex align-items-center border-bottom"
        onClick={() =>
          !option.disabled && !disabled && handleSelect(option.value)
        }
        style={{
          cursor: option.disabled || disabled ? "not-allowed" : "pointer",
        }}
      >
        <input
          id={`option-${option.value}`}
          type={isMultiple ? "checkbox" : "radio"}
          className="form-check-input"
          checked={isChecked}
          onChange={() => handleSelect(option.value)}
          onClick={(e) => e.stopPropagation()}
          disabled={option.disabled || disabled}
        />

        <label
          htmlFor={`option-${option.value}`}
          className="form-check-label ms-2 w-100"
          onClick={(e) => e.preventDefault()}
        >
          {option.label}
        </label>
      </div>
    );
  };

  return (
    <Form.Group>
      <div
        className="bg-white dropdown-container"
        style={{
          borderRadius: "10px",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <InputGroup className="p-2 border-bottom position-relative search-box align-items-center">
          {isMultiple && (
            <div className="form-check me-0" style={{paddingLeft:"9px"}}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={isAllSelected}
                onChange={handleSelectAll}
                disabled={disabled || flatFilteredOptions.length === 0}
              />
            </div>
          )}
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={disabled}
          />

          {searchTerm.length > 0 && !disabled && (
            <span
              onClick={() => setSearchTerm("")}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#aaa",
                fontSize: "18px",
                zIndex: 10,
                userSelect: "none",
              }}
            >
              <span className="material-symbols-outlined">close</span>
            </span>
          )}
        </InputGroup>

        <div
          className="options-scroll"
          style={{
            maxHeight: height,
            overflowY: "auto",
          }}
        >
          {isGrouped
            ? (filteredOptions as GroupedOption[]).map((group) => (
                <div key={group.group}>
                  <div className="fw-bold px-3 py-1 bg-light">
                    {group.group}
                  </div>
                  {group.options.map(renderOption)}
                </div>
              ))
            : (filteredOptions as Option[]).map(renderOption)}

          {filteredOptions?.length === 0 && (
            <div className="text-center text-muted p-2">No options found</div>
          )}
        </div>
      </div>
    </Form.Group>
  );
};

export default React.memo(SearchableListSelect);
